import { useEffect, useState } from 'react'
import { Button, message } from 'antd'
import { ExclamationCircleFilled, LogoutOutlined } from '@ant-design/icons'
import { useExitImpersonation } from '@/hooks/use-impersonation'

interface ImpersonatedUserInfo {
  id: string
  name: string
  phone: string
  roles: string[]
}

const IMP_ID_KEY = 'impersonation_id'
const IMP_USER_KEY = 'impersonated_user'
const ORIG_TOKEN_KEY = 'admin_original_token'
const ORIG_REFRESH_KEY = 'admin_original_refresh_token'

function readImpersonationState() {
  if (typeof window === 'undefined') {
    return { id: null as string | null, user: null as ImpersonatedUserInfo | null }
  }
  const id = localStorage.getItem(IMP_ID_KEY)
  const raw = localStorage.getItem(IMP_USER_KEY)
  let user: ImpersonatedUserInfo | null = null
  if (raw) {
    try {
      user = JSON.parse(raw) as ImpersonatedUserInfo
    } catch {
      user = null
    }
  }
  return { id, user }
}

/**
 * 化身模式全局提示条
 *
 * 挂载在主 Layout 顶部，当 localStorage 中存在 impersonation_id 时显示：
 *   "您正在以 XXX 身份操作中"
 *
 * 点击"退出"会调用 /impersonation/exit，恢复原 admin token，清理 localStorage 并刷新页面
 */
export function ImpersonationBanner() {
  const [state, setState] = useState(readImpersonationState)
  const exitMutation = useExitImpersonation()

  // 监听 storage 事件，支持多 Tab 同步
  useEffect(() => {
    const handler = () => setState(readImpersonationState())
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [])

  if (!state.id) return null

  const handleExit = async () => {
    try {
      const result = await exitMutation.mutateAsync({ impersonationId: state.id! })

      // 恢复管理员原始 token
      const originalToken = localStorage.getItem(ORIG_TOKEN_KEY)
      const originalRefresh = localStorage.getItem(ORIG_REFRESH_KEY)

      if (originalToken) {
        localStorage.setItem('access_token', originalToken)
      } else {
        // 理论上不会走到这里；兜底使用 exit 返回的 token
        localStorage.setItem('access_token', result.accessToken)
      }
      if (originalRefresh) {
        localStorage.setItem('refresh_token', originalRefresh)
      } else {
        localStorage.setItem('refresh_token', result.refreshToken)
      }

      localStorage.removeItem(IMP_ID_KEY)
      localStorage.removeItem(IMP_USER_KEY)
      localStorage.removeItem(ORIG_TOKEN_KEY)
      localStorage.removeItem(ORIG_REFRESH_KEY)

      message.success('已退出化身模式')
      window.location.href = '/dashboard'
    } catch (err: any) {
      message.error(err?.message || '退出化身失败')
    }
  }

  const name = state.user?.name || '该用户'
  const phone = state.user?.phone
  const roles = state.user?.roles?.join('、')

  return (
    <div className="bg-amber-500 text-white shadow-sm">
      <div className="flex items-center justify-between gap-4 px-4 py-2 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <ExclamationCircleFilled className="shrink-0" />
          <span className="truncate">
            您正在以 <strong>{name}</strong>
            {phone ? <span className="opacity-90">（{phone}）</span> : null}
            {roles ? <span className="opacity-75">「{roles}」</span> : null} 身份操作中，所有操作将被审计记录
          </span>
        </div>
        <Button
          size="small"
          danger
          type="primary"
          icon={<LogoutOutlined />}
          loading={exitMutation.isPending}
          onClick={handleExit}
        >
          退出化身
        </Button>
      </div>
    </div>
  )
}
